import React, { useEffect, useRef, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { Card } from 'react-bootstrap'
import cn from 'classnames'
import { escapeRegExp, throttle } from 'lodash'

import Searchbar from 'components/Shared/Searchbar'
import { SecondaryButton } from 'components/Shared/Button'
import { useTranslation, Trans } from 'react-i18next'
import { RemoveIcon } from 'images'
import useOnClickOutside from 'hooks/UseOnClickOutside'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import {
  permissionErrorProviderName,
  settingsPlatformsHref,
} from 'domain/platform/permissionErrorTarget'

const Row = ({ item, onChange }) => {
  return (
    <div
      key={item.id}
      className="search-dropdown_dropdown_results_item"
      onClick={() => onChange(item)}
    >
      <span>{item.name}</span>
    </div>
  )
}

Row.propTypes = {
  item: PropTypes.object,
  onChange: PropTypes.func,
}

const TryAgain = ({ error, loading, onClick, t, platform }) => {
  const providerName = permissionErrorProviderName(platform)
  return (
    <div className="search-dropdown_try-again">
      {loading ? (
        <LoadingSpinner
          title={t('Recommendations.campaign.fetchLoading', { platform: providerName })}
        />
      ) : (
        <span className="error">
          <Trans
            i18nKey={error}
            components={{
              a: (
                <a
                  href={settingsPlatformsHref(platform)}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="d-inline-block"
                  style={{ color: '#2F80ED' }}
                >
                  {providerName} assets page
                </a>
              ),
            }}
            values={{ provider: providerName }}
          >
            {t(error, { provider: providerName })}
          </Trans>
        </span>
      )}
      <SecondaryButton
        onClick={(e) => {
          e.preventDefault()
          onClick()
        }}
        size="small"
        text={t('common.tryAgain')}
        color="navy"
        disabled={loading}
        fullWidth={false}
      />
    </div>
  )
}

TryAgain.propTypes = {
  error: PropTypes.string,
  loading: PropTypes.bool,
  t: PropTypes.func,
  onClick: PropTypes.func,
  platform: PropTypes.oneOf(['meta', 'google']),
}

const SearchDropdown = ({
  value = null,
  onChange = () => {},
  placeholder = 'Search',
  inputPlaceholder,
  options = [],
  disabled = false,
  removeSelectedItem = () => {},
  onErrorBtnClick = () => {},
  loading = false,
  loadingValue = false,
  error = null,
  dropdownClasses = '',
  classNames = '',
  required = false,
  platform = 'meta',
}) => {
  const dropdownRef = useRef()
  const accordionRef = useRef()
  const dropdownListRef = useRef(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()

  let filteredItems = options
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((option) => {
      const matchSearchTerm = searchTerm
        ? option.name.toLowerCase().search(escapeRegExp(searchTerm?.toLowerCase())) > -1
        : true

      return matchSearchTerm
    })

  useOnClickOutside(dropdownRef, () => setDropdownOpen(false))

  useEffect(() => {
    const sideDrawerComponent = document.getElementById('side-drawer-content')

    const handleClick = () => {
      const expectedHeight =
        sideDrawerComponent.offsetHeight -
        accordionRef.current?.getBoundingClientRect().bottom -
        100

      if (dropdownListRef.current) {
        dropdownListRef.current.style.maxHeight =
          expectedHeight < 160 ? '160px' : expectedHeight + 'px'
        dropdownListRef.current.style.top = accordionRef.current?.offsetHeight
          ? accordionRef.current?.offsetHeight + 'px'
          : '40px'
      }
    }

    const throttledClick = throttle(handleClick, 200)

    if (sideDrawerComponent) {
      throttledClick()
      sideDrawerComponent.addEventListener('click', throttledClick)
    }

    return () => {
      if (sideDrawerComponent) sideDrawerComponent.removeEventListener('click', throttledClick)
    }
  }, [isDropdownOpen, dropdownListRef, accordionRef])

  const handleSelect = (item) => {
    onChange(item)
    setDropdownOpen(false)
  }

  // Distinct from the `loading` prop above (which only covers the open
  // dropdown's own options list) — this covers the closed, already-selected
  // value while a save triggered by picking it (or an auto-computed default)
  // is still in flight, so the field doesn't sit there looking interactive
  // while it's actually waiting on a network round trip.
  const isPending = disabled || loadingValue
  const optionsAvailable = options.length || error || loading
  const showResults = filteredItems.length || error || loading
  const defaultOption = options.find((option) => !!option.default)
  const selectedValueName = loadingValue
    ? t('common.loading')
    : value
      ? value.name
      : defaultOption?.name

  const handleRemoveSelectedItem = (e) => {
    e.stopPropagation()
    removeSelectedItem(e, value?.id)
    onChange({
      id: defaultOption?.id,
      name: defaultOption?.name,
      type: defaultOption?.type,
    })
  }

  return (
    <div className={`search-dropdown ${classNames}`} ref={dropdownRef}>
      <div ref={accordionRef} className="accordion">
        <Card
          className={cn({
            disabled_single: disabled,
          })}
          style={loadingValue ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <Card.Header
            as={Card.Header}
            onClick={() => !isPending && setDropdownOpen((prev) => !prev)}
            className="text-left"
          >
            <div className="search-dropdown_title">
              <div className="search-dropdown_title_selected">
                <div className="search-dropdown_title_selected_text">
                  {selectedValueName ? (
                    <span className="audience-name">{selectedValueName}</span>
                  ) : (
                    <span className="audience-name audience-name--placeholder">{inputPlaceholder}</span>
                  )}
                </div>
              </div>
              <div className="d-flex gap-3">
                {!isPending && !!value && (!required || (!!defaultOption && selectedValueName !== defaultOption.name)) && (
                  <span className="remove">
                    <RemoveIcon width={14} onClick={(e) => handleRemoveSelectedItem(e)} />
                  </span>
                )}
                {!isPending && (
                  <div className="align-self-center">
                    <div className={`accordion-arrow ${isDropdownOpen ? 'open' : ''}`} />
                  </div>
                )}
              </div>
            </div>
          </Card.Header>
        </Card>
      </div>
      {isDropdownOpen && !isPending && (
        <div className={`search-dropdown_dropdown ${dropdownClasses}`} ref={dropdownListRef}>
          {optionsAvailable ? (
            <div>
              <div className="search-dropdown_dropdown_searchbar">
                <Searchbar
                  handleChange={setSearchTerm}
                  placeholder={placeholder}
                  ariaLabel={placeholder}
                  postfixView={<i className="icon fa-solid fa-search" style={{ color: '#ccc' }}></i>}
                  defaultValue={searchTerm}
                />
              </div>
              <div className="search-dropdown_dropdown_results">
                {(loading || error) && (
                  <TryAgain
                    loading={loading}
                    error={error}
                    t={t}
                    onClick={onErrorBtnClick}
                    platform={platform}
                  />
                )}
                {showResults ? (
                  filteredItems.map((item) => {
                    return <Row key={item.id} item={item} onChange={handleSelect} />
                  })
                ) : (
                  <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
                    <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>
                      {t('common.noSearchResults')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
              <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>
                {t('common.noMoreFields')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SearchDropdown.propTypes = {
  defaultValue: PropTypes.string,
  value: PropTypes.object,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(object),
  onChange: PropTypes.func,
  onErrorBtnClick: PropTypes.func,
  removeSelectedItem: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  loadingValue: PropTypes.bool,
  dropdownClasses: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  classNames: PropTypes.string,
  required: PropTypes.bool,
  platform: PropTypes.oneOf(['meta', 'google']),
}

export default SearchDropdown
